import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ChangeDetectionStrategy,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Subscription } from "rxjs";
import { ConversationType, UserActivityModel } from "types";
import { generateUpgradeUrl } from "utils/url-utils";
import {
  AccountService,
  AnalyticsService,
  ConversationsService,
  FirebaseAnalyticsService,
  UserService,
} from "../../core";
import { AlertComponent } from "../../core/components/alert/alert.component";

@Component({
  selector: "app-activity-feed-list-item",
  templateUrl: "./activity-feed-list-item.component.html",
  styleUrls: ["./activity-feed-list-item.component.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ActivityFeedListItemComponent
  implements OnChanges, OnInit, OnDestroy
{
  @Input() activity: UserActivityModel;

  public routerLink: string[] = [];

  public get effectiveRouterLink(): string[] | null {
    if (
      this.activity?.type === "Mention" &&
      this.historyLimitCutoffDate !== null &&
      this.activity.timeStampUtc &&
      new Date(this.activity.timeStampUtc) < this.historyLimitCutoffDate
    ) {
      return null;
    }
    return this.routerLink;
  }

  private historyLimitCutoffDate: Date | null = null;
  private historyLimitDays: number | null = null;
  private userLimitsSub!: Subscription;

  constructor(
    private analyticsService: FirebaseAnalyticsService,
    private conversationsService: ConversationsService,
    private userService: UserService,
    private matDialog: MatDialog,
    private accountService: AccountService,
    private celoAnalyticsService: AnalyticsService
  ) {}

  public ngOnInit(): void {
    this.userLimitsSub = this.accountService.userLimits$.subscribe({
      next: (userLimits) => {
        const maxDays = userLimits?.message_history_limit?.maxDays;
        if (typeof maxDays === "number" && maxDays > 0) {
          this.historyLimitDays = maxDays;
          this.historyLimitCutoffDate = new Date(
            Date.now() - maxDays * 86400000
          );
        } else {
          this.historyLimitDays = null;
          this.historyLimitCutoffDate = null;
        }
      },
    });
  }

  public ngOnDestroy(): void {
    this.userLimitsSub?.unsubscribe();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!changes.activity) return;
    const activity = changes.activity.currentValue as UserActivityModel;

    switch (activity.type) {
      case "WorkspaceJoined":
        this.routerLink = ["/network/workspace", this.activity.metadata.id];
        break;
      case "TeamOnOffCall":
        this.routerLink = ["/roles", this.activity.metadata.id];
        break;
      case "Mention":
        const currentUserId = this.userService.getUserId();

        // Navigate to roles tab if this mention is for a role the current user is a member of
        if (
          currentUserId &&
          activity.metadata.conversationType === ConversationType.TeamChat &&
          this.conversationsService.isUserMemberOfTeamForTeamChat(
            currentUserId,
            activity.metadata.conversationId
          )
        ) {
          const teamId = this.conversationsService.tryParsePotentialTeamId(
            this.activity.metadata.conversationId
          );
          this.routerLink = [
            "/roles",
            teamId,
            "conversations",
            this.activity.metadata.conversationId,
            "messages",
          ];

          break;
        }

        if (
          this.activity.metadata.conversationType === ConversationType.External
        ) {
          this.routerLink = [
            "/external",
            this.activity.metadata.conversationId,
            "messages",
          ];
          break;
        }

        this.routerLink = [
          "/conversations",
          this.activity.metadata.conversationId,
          "messages",
        ];
        break;
      default:
        this.routerLink = [];
        break;
    }
  }

  itemClicked = () => {
    this.analyticsService.buttonClickEvent("activity_feed_item", {
      type: this.activity.type,
      read: this.activity.metadata.read,
    });

    if (
      this.activity.type === "Mention" &&
      this.historyLimitCutoffDate !== null &&
      this.activity.timeStampUtc &&
      new Date(this.activity.timeStampUtc) < this.historyLimitCutoffDate
    ) {
      const dialogRef = AlertComponent.openDialog(this.matDialog, {
        title: "Free plan message history limit reached",
        message: `This message is older than ${this.historyLimitDays} days and isn't available on your current plan. Unlock full access to view your complete message history.`,
        acceptOnly: false,
        acceptButtonText: "Upgrade",
        acceptUrl: generateUpgradeUrl(),
        reverseButtonOrder: true,
      });
      dialogRef.afterOpened().subscribe(() => {
        this.celoAnalyticsService
          .logEvent({
            eventType: "FreePlanLimitPromptView",
            timestamp: new Date().toISOString(),
            limitType: "MessageHistory",
            referrer: "ActivityFeed",
          })
          .subscribe();
      });
      dialogRef.afterClosed().subscribe((result) => {
        this.celoAnalyticsService
          .logEvent({
            eventType: "FreePlanLimitCtaClick",
            timestamp: new Date().toISOString(),
            limitType: "MessageHistory",
            referrer: "ActivityFeed",
            ctaType: result ? "ViewPlans" : "Dismiss",
          })
          .subscribe();
      });
      return;
    }
  };
}
